import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { SCROLL_THRESHOLD_PX } from "../../lib/constants";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#apartments", label: t.nav.apartments },
    { href: "#amenities", label: t.nav.amenities },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#location", label: t.nav.location },
    { href: "#booking", label: t.nav.booking },
    { href: "#contact", label: t.nav.contact },
  ];

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-[#E9E2D3]/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link
          to="/"
          data-testid="brand-link"
          className={`font-serif text-xl sm:text-2xl md:text-3xl tracking-tight whitespace-nowrap ${
            scrolled ? "text-[#1A2B4C]" : "text-white"
          }`}
        >
          Sithonia <span className="italic font-light">Holidays</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className={`text-sm tracking-wide transition-colors hover:opacity-70 ${
                scrolled ? "text-[#1A2B4C]" : "text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setLang(lang === "el" ? "en" : "el")}
            data-testid="language-switcher"
            className={`flex items-center gap-1.5 text-sm tracking-wide px-3 py-1.5 rounded-full border transition-all ${
              scrolled
                ? "border-[#1A2B4C]/30 text-[#1A2B4C] hover:bg-[#1A2B4C] hover:text-white"
                : "border-white/40 text-white hover:bg-white hover:text-[#1A2B4C]"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            {lang.toUpperCase()}
          </button>

          <button
            onClick={() => go("#booking")}
            data-testid="navbar-book-now-btn"
            className="hidden md:inline-flex bg-[#1A2B4C] text-white text-sm tracking-wide px-5 py-2.5 rounded-full hover:bg-[#1A2B4C]/90 transition-all hover:-translate-y-0.5"
          >
            {t.nav.bookNow}
          </button>

          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${scrolled ? "text-[#1A2B4C]" : "text-white"}`}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E9E2D3]"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="text-left py-2 text-[#1A2B4C] text-base"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#booking")}
              className="mt-2 bg-[#1A2B4C] text-white py-3 rounded-full"
            >
              {t.nav.bookNow}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
